import { useEffect, useRef } from "react";

import { Add, Error, ReplayOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";

import { ChatMessageWithParts, ImageContent } from "src/views/assistant/types";
import { getLastUserMessage } from "../ChatStreaming/services/chatHelpers";
import { useSyncRemoteModel } from "src/views/models/ModelService";
import { ErrorMetadata } from "../types/chatTypes";

const ChatErrorMessage = ({
  dialogue,
  chatStreamError,
  isChatConnectionError,
  errorMetadata,
  startNewChat,
  resendUserMessage,
  onRetryConnection,
}: {
  dialogue: ChatMessageWithParts[];
  chatStreamError: string;
  isChatConnectionError: boolean;
  errorMetadata?: ErrorMetadata | null;
  startNewChat: () => void;
  resendUserMessage: (
    id: string,
    text: string,
    images?: ImageContent[],
  ) => void;
  /** Retry the SSE connection after a transport error (optional) */
  onRetryConnection?: () => void;
}) => {
  const recentUserMessage = getLastUserMessage(dialogue);
  const { mutate: syncRemoteModel } = useSyncRemoteModel();
  const hasSyncedRef = useRef(false);

  // Auto-sync model when empty response error is received (best-effort recovery)
  useEffect(() => {
    if (
      errorMetadata?.error_code === "EMPTY_LLM_RESPONSE" &&
      errorMetadata?.model_id &&
      !hasSyncedRef.current
    ) {
      hasSyncedRef.current = true;
      syncRemoteModel(errorMetadata.model_id, {
        onError: () => {
          console.warn("Model sync failed, user may need to retry manually");
        },
      });
    }
  }, [errorMetadata, syncRemoteModel]);

  // Reset sync flag when error clears
  useEffect(() => {
    if (!chatStreamError && !isChatConnectionError) {
      hasSyncedRef.current = false;
    }
  }, [chatStreamError, isChatConnectionError]);

  const handleResendUserMessage = () => {
    if (recentUserMessage) {
      resendUserMessage(
        recentUserMessage.id || "",
        recentUserMessage.contentParts?.[0]?.text ?? "",
        recentUserMessage.images,
      );
    }
  };

  // Determine error display based on error code
  const getErrorDisplay = (): {
    title: string;
    showNewChatOnly: boolean;
    message?: string;
  } => {
    const errorCode = errorMetadata?.error_code;
    const requiresNewChat = errorMetadata?.requires_new_chat;

    if (errorCode === "CONVERSATION_TOO_LONG" || requiresNewChat) {
      return {
        title: "Conversation Too Long",
        showNewChatOnly: true,
      };
    } else if (errorCode === "MESSAGE_TOO_LONG") {
      return {
        title: "Message Too Long",
        showNewChatOnly: false,
      };
    } else if (errorCode === "EMPTY_LLM_RESPONSE") {
      return {
        title: "Empty Response",
        showNewChatOnly: false,
        message:
          "The model returned an empty response. This may be a temporary issue — click Regenerate to retry, or start a new chat if the problem persists.",
      };
    } else if (errorCode === "DATASET_NOT_READY") {
      return {
        title: "File Not Ready",
        showNewChatOnly: false,
      };
    }

    return {
      title: "Something went wrong",
      showNewChatOnly: false,
    };
  };

  const { title, showNewChatOnly, message: customMessage } = getErrorDisplay();

  // Extract clean error message from chatStreamError
  const getErrorMessage = () => {
    const parts: string[] = [];

    if (chatStreamError) {
      // chatStreamError should already be just the message string from CoreStreamingSlice
      // but fallback to parsing if it's somehow an object
      if (typeof chatStreamError === "string") {
        parts.push(chatStreamError);
      } else if (
        typeof chatStreamError === "object" &&
        chatStreamError !== null
      ) {
        // Fallback: if somehow an object, extract message
        parts.push(
          (chatStreamError as any).message || JSON.stringify(chatStreamError),
        );
      }
    }

    if (isChatConnectionError && !chatStreamError) {
      parts.push("Connection error");
    }

    return parts.join(" • ");
  };

  return (
    <>
      {(chatStreamError || isChatConnectionError) && (
        <Card variant="outlined" sx={{ mb: "3%", width: "100%" }}>
          <CardContent>
            <Stack direction="row" alignItems="flex-start" gap={2}>
              <Error color="error" />
              <Box>
                <Typography variant="body1" fontWeight="bold" component="div">
                  {title}
                </Typography>
                <Typography
                  mt={1}
                  variant="body1"
                  sx={{ color: "text.secondary" }}
                >
                  {customMessage || getErrorMessage()}
                </Typography>
                <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
                  {isChatConnectionError && onRetryConnection && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disableElevation
                      startIcon={<ReplayOutlined />}
                      onClick={onRetryConnection}
                      data-attr="assistant:btn:retry-connection"
                    >
                      Retry connection
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    startIcon={<Add />}
                    onClick={startNewChat}
                    data-attr="assistant:btn:new-chat-from-error"
                  >
                    New chat
                  </Button>
                  {!showNewChatOnly && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disableElevation
                      startIcon={<ReplayOutlined />}
                      onClick={handleResendUserMessage}
                      data-attr="assistant:btn:regenerate-from-error"
                    >
                      Regenerate
                    </Button>
                  )}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ChatErrorMessage;
